import {
  COMMUNUCATION_CHANNEL,
  RECEIVE_LIST_REQUESTED_DOCUMENTS,
  RECEIVE_SAVED_DOCUMENTS,
  REQUEST_CREATE_REQUEST_FROM_TEMPLATE,
  REQUEST_CREATE_DEMO_REQUEST,
  REQUEST_LIST_REQUESTED_DOCUMENTS,
  REQUEST_LIST_TEMPLATE_DOCUMENTS,
  REQUEST_SAVED_DOCUMENTS,
  SET_TEMPLATE_MODE_OFF,
  RECEIVE_CREATE_DEMO_REQUEST
} from '../actions/rootAction';

import {
  DOC_TYPE_DOCUMENT,
  DOC_TYPE_FORM,
  DOC_TYPE_ID_CHECK,
  DOC_TYPE_IMMEDIATE_CONFIRMATION,
  DOC_TYPE_INFORMATION,
  DOC_TYPE_PDF_FORM,
  ID_CHECK_TYPE_PASS,
  isDocumentSubmitted,
  isNotEmptyArray
} from '../../components/helpers/utils';
import {
  nl2br,
  prepareBrandingDetails,
  prepareRequestedDocuments,
  prepareExternalLinks,
  defineFormStructureRecipientsAndValuesInheritanceAndSpecialSignatures,
  defineCurrentRecipientRoles,
  timeStampToDateString
} from '../utils';
import { localeLangToDictionary } from '../../components/translate/dictionary';
import { REQUEST_TYPE } from '../../lib/utils';

const requests = (state, action) => {
  let expired = onDate => (onDate ? new Date() > new Date(onDate) : false);

  switch (action.type) {
    case REQUEST_LIST_REQUESTED_DOCUMENTS:
    case REQUEST_LIST_TEMPLATE_DOCUMENTS:
    case REQUEST_CREATE_DEMO_REQUEST:
      return Object.assign({}, state, {
        caseName: null,
        infoMessage: null,
        errorMessage: null,
        isFetching: true
      });

    case RECEIVE_CREATE_DEMO_REQUEST:
      return Object.assign({}, state, {
        recipientRequestId: action.recipientRequestId,
        isFetching: false,
        demoRequest: true
      });

    case REQUEST_SAVED_DOCUMENTS:
      return Object.assign({}, state, {
        isFetching: true
      });

    case REQUEST_CREATE_REQUEST_FROM_TEMPLATE:
      return Object.assign({}, state, {
        isFetching: true,
        templateRequestPreparedError: false
      });

    case RECEIVE_SAVED_DOCUMENTS: {
      const savedDocs = action.requestedDocuments;
      if (!savedDocs) return Object.assign({}, state, { isFetching: false });

      const restoredPages = new Map();
      const restoredForms = new Map();
      const restoredStatus = new Map();

      savedDocs.forEach(doc => {
        const originalDoc = state.requestedDocuments.get(doc.id);
        const submitted = isDocumentSubmitted(originalDoc);

        switch (originalDoc.type) {
          case DOC_TYPE_DOCUMENT:
          case DOC_TYPE_ID_CHECK:
            if (!submitted) {
              const restoredPagesEntry = new Map();
              doc.attributes.attachments &&
                doc.attributes.attachments.forEach((pageId, index) =>
                  restoredPagesEntry.set(
                    pageId,
                    doc.attributes.attachmentUrls[index]
                  )
                );
              restoredPages.set(doc.id, restoredPagesEntry);

              if (originalDoc.type === DOC_TYPE_ID_CHECK) {
                restoredStatus.set(doc.id, {
                  idCheckDocumentType: doc.attributes.idDocumentType
                    ? doc.attributes.idDocumentType
                    : restoredPagesEntry.size > 0
                    ? ID_CHECK_TYPE_PASS
                    : ''
                });
              }
            }
            break;

          case DOC_TYPE_FORM:
          case DOC_TYPE_PDF_FORM:
            if (submitted) {
              restoredForms.set(doc.id, state.forms.get(doc.id));
            } else {
              //find all AES signatures
              const {
                aesForCurrentRecipient
              } = defineFormStructureRecipientsAndValuesInheritanceAndSpecialSignatures(
                originalDoc.attributes.formStructure,
                defineCurrentRecipientRoles(
                  state.recipientId,
                  originalDoc.recipientInfos
                ),
                originalDoc.type === DOC_TYPE_FORM,
                originalDoc.id,
                originalDoc.attributes?.qualifiedSigned
              );

              const preparedData = new Map(
                Object.entries(doc.attributes.formData)
              );

              // remove advanced signatures while touchData does not save in cache
              if (aesForCurrentRecipient?.length > 0) {
                aesForCurrentRecipient.forEach(item => {
                  if (item.collectBiometricData) {
                    preparedData.delete(item.id);
                  }
                });
              }

              restoredForms.set(doc.id, preparedData);
            }

            break;

          case DOC_TYPE_INFORMATION:
          case DOC_TYPE_IMMEDIATE_CONFIRMATION:
            restoredStatus.set(doc.id, doc.attributes);

          //no default
        }
      });

      return Object.assign({}, state, {
        isFetching: false,
        forms: restoredForms,
        scannedDocuments: restoredPages,
        documentsStatus: restoredStatus,
        requestIsRestored: true
      });
    }

    case SET_TEMPLATE_MODE_OFF: {
      return Object.assign({}, state, {
        templateMode: false
      });
    }

    case RECEIVE_LIST_REQUESTED_DOCUMENTS: {
      const closed = action.closed || expired(action.expirationDate);

      const formsData = state.forms;
      const documentsStatus = state.documentsStatus;
      const scannedDocuments = state.scannedDocuments;

      const {
        preparedDocuments,
        preparedFormsData
      } = prepareRequestedDocuments(
        action.recipientId,
        action.requestedDocuments,
        formsData,
        action.valueInheritance
      );

      const usersData = isNotEmptyArray(action.recipients)
        ? action.recipients.map(r => ({
            id: r.id,
            role: r.recipientRole,
            current: r.id === action.recipientId,
            userName: undefined,
            userEmail: undefined,
            userPhone: undefined,
            recipientName: r.recipientName,
            recipientEmail: r.recipientEmail,
            recipientPhone: r.recipientPhone,
            userFields: undefined,
            conditionId: r.conditionId
          }))
        : state.usersData;

      usersData[0].userFields = action.embeddingFields.filter(
        f => f.id && f.title
      );

      window.userLang =
        state.requestStartInfo?.params?.lang ||
        (action.language
          ? localeLangToDictionary(action.language)
          : state.selectedLang);

      const preparedBrandingDetails = prepareBrandingDetails(
        action.brandingDetails
      );
      const paperflyRequestProgress =
        !action.submitted && action.paperflyRequestProgress?.[0];

      const completedPageIndex = action.requestPages
        ? action.requestPages.findIndex(
            page =>
              page.pageId === paperflyRequestProgress?.pageId &&
              paperflyRequestProgress?.state === 'COMPLETED'
          )
        : -1;

      const fixedRequestPages = action.requestPages?.map((page, index) => ({
        ...page,
        submitted: index <= completedPageIndex,
        submittedDate: paperflyRequestProgress
          ? timeStampToDateString(paperflyRequestProgress.creationTime, true)
          : '',
        documents: page.documents?.filter(docId =>
          action.requestedDocuments?.some(rd => rd.id === docId)
        )
      }));
      const embedOverwrite = {};

      if (state.requestStartInfo?.params?.embed === 'true') {
        preparedBrandingDetails.embedBackground =
          state.requestStartInfo?.params?.embedBackground;
        preparedBrandingDetails.embedMode = true;
        embedOverwrite.skipStartPage = true;
        embedOverwrite.skipStartPageOriginal =
          action.skipStartPage || action.recipientId === 'REQUESTER_SELF';
        if (!action.requestPages) {
          embedOverwrite.requestPages = action.requestedDocuments?.map(d => ({
            pageId: d.id,
            title: d.name,
            documents: [d.id]
          }));
        }
      }
      return Object.assign({}, state, {
        embedMode: state.requestStartInfo.params?.embed === 'true',
        hideNavigation: state.requestStartInfo.params?.hidenav === 'true',
        requesterSelfMode:
          state.requestStartInfo.params?.embed === 'true' &&
          action.recipientId === 'REQUESTER_SELF',
        demoRequest:
          state.requestStartInfo.requestType === REQUEST_TYPE.DEMO_TEMPLATE,
        selectedLang: window.userLang,
        region: action.region,
        forms: preparedFormsData,
        documentsStatus: documentsStatus,
        scannedDocuments: scannedDocuments,
        templateRequestPrepared: action.templateRequestPrepared,
        isFetching: false,
        postalChannel:
          action.communicationChannel === COMMUNUCATION_CHANNEL.POSTAL,
        inPersonChannel:
          action.communicationChannel === COMMUNUCATION_CHANNEL.IN_PERSON,
        customizationKey: action.customizationKey,
        closed: closed,
        requestState: action.state,
        scanEnded: action.submitted,
        submitted: action.submitted,
        submittedDate: action.submittedDate
          ? timeStampToDateString(action.submittedDate, true)
          : '',

        recipientRequestId: action.recipientRequestId,
        recipientId: action.recipientId,
        recipientEmail: action.recipientEmail,
        recipientPhone: action.recipientPhone,
        recipientName: action.recipientName,
        caseName: action.caseName,
        casePasswordHint: null, //'Please input your date of birth',
        requestedDocuments: preparedDocuments,
        infoMessage: action.infoMessage,
        errorCode: action.errorCode,
        actionButtonLabel: action.actionButtonLabel,
        completeButtonLabel: action.completeButtonLabel,
        customConfirmationPageText: action.customConfirmationPageText
          ? prepareExternalLinks(action.customConfirmationPageText)
          : null,
        companyLogo: action.companyLogo,
        companyName: action.companyName,
        companyEmail: action.companyEmail,
        endDate: action.expirationDate
          ? timeStampToDateString(action.expirationDate)
          : '',
        emailMessage: action.emailMessage
          ? prepareExternalLinks(nl2br(action.emailMessage))
          : null,
        requestMessage: prepareExternalLinks(action.requestMessage),
        expired: action.errorCode === 'REQUEST_EXPIRED',
        brandingDetails: preparedBrandingDetails,
        analyticsDisabled: action.analyticsDisabled,
        pwaCanApplySignature: action.pwaCanApplySignature,
        usersData: usersData,
        qesProvider: action.qesProvider,
        qesFirstName: action.qesFirstName,
        qesLastName: action.qesLastName,
        qesValueInheritance: action.qesValueInheritance,
        canSaveDocuments:
          action.canSaveDocuments && !(closed || action.submitted),
        oneDocumentMode: preparedDocuments && preparedDocuments.size === 1,
        templateMode: action.templateMode,
        documentRejectionAllowed: action.documentRejectionAllowed,
        authenticationState: action.authenticationState,
        authenticationType: action.authenticationType,
        authOnStartRequired:
          action.authenticationState === 'REQUIRED_BEFORE_LIST',
        requestPages: fixedRequestPages,
        documentConditions: action.documentConditions,
        recipientConditions: action.recipientConditions,
        valueInheritance: action.valueInheritance,
        pagesCompletedDate: paperflyRequestProgress
          ? timeStampToDateString(paperflyRequestProgress.creationTime, true)
          : '',
        skipStartPage:
          action.skipStartPage || state.crossDeviceRequest !== null,
        embeddingFieldsInheritance: action.embeddingFieldsInheritance,
        currentRequestPageIndex:
          completedPageIndex !== -1
            ? completedPageIndex + 1
            : state.currentRequestPageIndex,
        ...embedOverwrite
      });
    }

    default:
      return state;
  }
};

export default requests;
